'use client';

import { useState, useEffect } from 'react';
import ProjectCard from './ProjectCard';
import FilterBar from './FilterBar';
import ProjectModal from './ProjectModal';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';


interface Project {
  id: string;
  title: string;
  location: string;
  type: string;
  style: string;
  budget: string;
  area: string;
  image: string;
  alt: string;
  description: string;
  completionYear: number;
  detailedDescription: string;
  challenges: string[];
  solutions: string[];
  materials: string[];
  timeline: string;
  clientTestimonial: string;
  clientName: string;
  beforeImage: string;
  beforeAlt: string;
  gallery: Array<{image: string;alt: string;}>;
  videos?: Array<{url: string;title: string;}>;
}

const mockProjects: Project[] = [{
  id: '5',
  title: 'The Alabaster Home',
  location: 'Purva Atmosphere Bangalore | Puravankara Thanisandra Main Road',
  type: 'apartment',
  style: 'contemporary',
  budget: '40-60',
  area: '2,800 sq.ft',
  image: '/Alabaster-Home/4.png',
  alt: 'The Alabaster Home living area',
  description: 'A contemporary apartment project at Purva Atmosphere, showcasing a soft alabaster palette and layered textures.',
  completionYear: 2025,
  detailedDescription: 'The Alabaster Home embraces calm, neutral tones with sculptural millwork and warm natural finishes to create a sophisticated, livable interior.',
  challenges: [
    'Maximizing light in a deep plan',
    'Coordinating custom joinery finishes',
    'Balancing neutral palettes with warmth'
  ],

  solutions: [
    'Optimized light with reflective surfaces and layered lighting',
    'Specified mixed-wood tones and textured upholstery',
    'Used warm metals and natural fibres for contrast'
  ],

  materials: ['Light Teak', 'Engineered Stone', 'Limestone Finish', 'Brass Hardware', 'Linen Textiles'],
  timeline: '7 months from concept to completion',
  clientTestimonial: 'Our home feels like a calm retreat—sophisticated yet warm.',
  clientName: 'Puravankara Homeowner',
  beforeImage: '/Alabaster-Home/1.png',
  beforeAlt: 'Before view of the apartment',
  gallery: [
    { image: '/Alabaster-Home/4.png', alt: 'Alabaster Home - Living area 1' },
    { image: '/Alabaster-Home/5.png', alt: 'Alabaster Home - Living area 2' },
    { image: '/Alabaster-Home/3.png', alt: 'Alabaster Home - Living area 3' },
    { image: '/Alabaster-Home/2.png', alt: 'Alabaster Home - Living area 5' },
    { image: '/Alabaster-Home/6.png', alt: 'Alabaster Home - Kitchen 6' },
    { image: '/Alabaster-Home/7.png', alt: 'Alabaster Home - Bedroom 7' },
    { image: '/Alabaster-Home/8.png', alt: 'Alabaster Home - Bedroom 8' },
    { image: '/Alabaster-Home/9.png', alt: 'Alabaster Home - Bathroom 9' },
    { image: '/Alabaster-Home/10.png', alt: 'Alabaster Home - Feature 10' },
    { image: '/Alabaster-Home/11.png', alt: 'Alabaster Home - Feature 11' },
    { image: '/Alabaster-Home/12.png', alt: 'Alabaster Home - Detail 12' },
    { image: '/Alabaster-Home/13.png', alt: 'Alabaster Home - Detail 13' },
    { image: '/Alabaster-Home/14.png', alt: 'Alabaster Home - Detail 14' },
    { image: '/Alabaster-Home/15.png', alt: 'Alabaster Home - Detail 15' },
    { image: '/Alabaster-Home/16.png', alt: 'Alabaster Home - Detail 16' },
    { image: '/Alabaster-Home/17.png', alt: 'Alabaster Home - Detail 17' },
    { image: '/Alabaster-Home/18.png', alt: 'Alabaster Home - Detail 18' },
    { image: '/Alabaster-Home/19.png', alt: 'Alabaster Home - Feature 19' },
    { image: '/Alabaster-Home/1.jpeg', alt: 'Alabaster Home - Photograph 1' },
    { image: '/Alabaster-Home/3.jpeg', alt: 'Alabaster Home - Photograph 3' },
    { image: '/Alabaster-Home/4.jpeg', alt: 'Alabaster Home - Photograph 4' },
    { image: '/Alabaster-Home/5.jpeg', alt: 'Alabaster Home - Photograph 5' },
    { image: '/Alabaster-Home/6.jpeg', alt: 'Alabaster Home - Photograph 6' },
    { image: '/Alabaster-Home/7.jpeg', alt: 'Alabaster Home - Photograph 7' },
    { image: '/Alabaster-Home/8.jpeg', alt: 'Alabaster Home - Photograph 8' },
    { image: '/Alabaster-Home/9.jpeg', alt: 'Alabaster Home - Photograph 9' }
  ],
  videos: [
    { url: 'https://www.youtube.com/shorts/buc1W0X8Aso', title: 'Walkthrough' },
    { url: 'https://www.youtube.com/shorts/WQKlqJe1QXE', title: 'Living & Dining' },
    { url: 'https://www.youtube.com/shorts/lGMhm0B4X6U', title: 'Kitchen' },
    { url: 'https://www.youtube.com/shorts/n84ImznqRmw', title: 'Daughter Room' }
  ]
},
{
  id: '6',
  title: 'The Statura Residence',
  location: 'Krishvi Statura, Bangalore',
  type: 'apartment',
  style: 'contemporary',
  budget: '40-60',
  area: '2,600 sq.ft',
  image: '/The-Statura-Residence/1.jpg',
  alt: 'The Statura Residence living area',
  description: 'A contemporary apartment at Krishvi Statura designed with clean lines, warm textures, and functional luxury.',
  completionYear: 2025,
  detailedDescription: 'The Statura Residence brings together a refined material palette with practical, family-friendly planning. Custom joinery, layered lighting, and a restrained colour palette give every room a calm, contemporary feel while staying rooted in comfortable, everyday living.',
  challenges: [
    'Optimizing layout for everyday family use',
    'Balancing a contemporary look with warmth',
    'Coordinating custom furniture and finishes on schedule'
  ],
  solutions: [
    'Reworked space planning for better flow and storage',
    'Introduced warm wood tones alongside cool contemporary finishes',
    'Closely tracked vendor timelines for on-time delivery'
  ],
  materials: ['Engineered Wood', 'Laminate Finishes', 'Quartz Countertops', 'Matte Metal Hardware', 'Textured Fabrics'],
  timeline: '6 months from concept to completion',
  clientTestimonial: 'Hayat Interiors gave our home a look that feels both modern and inviting.',
  clientName: 'Krishvi Statura Homeowner',
  beforeImage: '/The-Statura-Residence/1.jpg',
  beforeAlt: 'Before view of the apartment',
  gallery: [
    { image: '/The-Statura-Residence/1.jpg', alt: 'Statura Residence - Living area' },
    { image: '/The-Statura-Residence/2.jpg', alt: 'Statura Residence - Living area 2' },
    { image: '/The-Statura-Residence/3.jpg', alt: 'Statura Residence - Dining area' },
    { image: '/The-Statura-Residence/4.jpg', alt: 'Statura Residence - Kitchen' },
    { image: '/The-Statura-Residence/5.jpg', alt: 'Statura Residence - Kitchen detail' },
    { image: '/The-Statura-Residence/7.jpg', alt: 'Statura Residence - Bedroom' },
    { image: '/The-Statura-Residence/8.jpg', alt: 'Statura Residence - Bedroom detail' },
    { image: '/The-Statura-Residence/9.jpg', alt: 'Statura Residence - Bedroom 2' },
    { image: '/The-Statura-Residence/10.jpg', alt: 'Statura Residence - Wardrobe' },
    { image: '/The-Statura-Residence/11.jpg', alt: 'Statura Residence - Bathroom' },
    { image: '/The-Statura-Residence/12.jpg', alt: 'Statura Residence - Feature wall' },
    { image: '/The-Statura-Residence/13.jpg', alt: 'Statura Residence - Foyer' },
    { image: '/The-Statura-Residence/14.jpg', alt: 'Statura Residence - Detail' },
    { image: '/The-Statura-Residence/15.jpg', alt: 'Statura Residence - Detail 2' },
    { image: '/The-Statura-Residence/16.jpg', alt: 'Statura Residence - Detail 3' },
    { image: '/The-Statura-Residence/17.jpg', alt: 'Statura Residence - Detail 4' },
    { image: '/The-Statura-Residence/18.jpg', alt: 'Statura Residence - Detail 5' },
    { image: '/The-Statura-Residence/19.jpg', alt: 'Statura Residence - Detail 6' },
    { image: '/The-Statura-Residence/20.jpg', alt: 'Statura Residence - Detail 7' },
    { image: '/The-Statura-Residence/21.jpg', alt: 'Statura Residence - Detail 8' }
  ]
},
{
  id: '7',
  title: 'The Willow Residence',
  location: 'Purva Atmosphere Bangalore | Puravankara Thanisandra Main Road',
  type: 'apartment',
  style: 'contemporary',
  budget: '40-60',
  area: '2,800 sq.ft',
  image: '/The-Willow-Residence/willow_0.jpg',
  alt: 'The Willow Residence living area',
  description: 'A warm, contemporary apartment at Purva Atmosphere blending natural textures with soft, livable interiors.',
  completionYear: 2025,
  detailedDescription: 'The Willow Residence takes a warmer, nature-inspired approach within the same Purva Atmosphere community — soft wood tones, woven textures, and gentle lighting come together for a relaxed, contemporary home.',
  challenges: [
    'Creating a distinct identity within a shared building layout',
    'Bringing warmth into a compact floor plan',
    'Sequencing civil and furnishing work efficiently'
  ],
  solutions: [
    'Used custom millwork and lighting to give the home its own character',
    'Layered natural wood and woven textiles throughout',
    'Phased execution to keep the project on schedule'
  ],
  materials: ['Natural Wood Veneer', 'Woven Textiles', 'Engineered Stone', 'Matte Brass Accents', 'Soft Furnishings'],
  timeline: '7 months from concept to completion',
  clientTestimonial: 'Our home feels warm, calm, and completely us.',
  clientName: 'Purva Atmosphere Homeowner',
  beforeImage: '/The-Willow-Residence/willow_0.jpg',
  beforeAlt: 'Before view of the apartment',
  gallery: [
    { image: '/The-Willow-Residence/willow_0.jpg', alt: 'Willow Residence - Living area' },
    { image: '/The-Willow-Residence/willow_1.jpeg', alt: 'Willow Residence - Living area 2' },
    { image: '/The-Willow-Residence/willow_2.jpeg', alt: 'Willow Residence - Dining area' },
    { image: '/The-Willow-Residence/willow_3.jpeg', alt: 'Willow Residence - Kitchen' },
    { image: '/The-Willow-Residence/willow_4.jpeg', alt: 'Willow Residence - Kitchen detail' },
    { image: '/The-Willow-Residence/willow_5.jpeg', alt: 'Willow Residence - Bedroom' },
    { image: '/The-Willow-Residence/willow_6.jpeg', alt: 'Willow Residence - Bedroom detail' },
    { image: '/The-Willow-Residence/willow_7.jpeg', alt: 'Willow Residence - Bedroom 2' },
    { image: '/The-Willow-Residence/willow_8.jpeg', alt: 'Willow Residence - Wardrobe' },
    { image: '/The-Willow-Residence/willow_9.jpeg', alt: 'Willow Residence - Bathroom' },
    { image: '/The-Willow-Residence/willow_10.jpeg', alt: 'Willow Residence - Feature wall' },
    { image: '/The-Willow-Residence/willow_11.jpeg', alt: 'Willow Residence - Foyer' },
    { image: '/The-Willow-Residence/willow_12.jpeg', alt: 'Willow Residence - Detail' },
    { image: '/The-Willow-Residence/willow_13.jpeg', alt: 'Willow Residence - Detail 2' },
    { image: '/The-Willow-Residence/willow_14.jpeg', alt: 'Willow Residence - Detail 3' },
    { image: '/The-Willow-Residence/willow_15.jpeg', alt: 'Willow Residence - Detail 4' },
    { image: '/The-Willow-Residence/willow_16.jpeg', alt: 'Willow Residence - Detail 5' },
    { image: '/The-Willow-Residence/willow_17.jpeg', alt: 'Willow Residence - Detail 6' },
    { image: '/The-Willow-Residence/willow_18.jpeg', alt: 'Willow Residence - Detail 7' },
    { image: '/The-Willow-Residence/willow_19.jpeg', alt: 'Willow Residence - Detail 8' },
    { image: '/The-Willow-Residence/willow_20.jpeg', alt: 'Willow Residence - Detail 9' },
    { image: '/The-Willow-Residence/willow_21.jpeg', alt: 'Willow Residence - Detail 10' },
    { image: '/The-Willow-Residence/willow_22.jpeg', alt: 'Willow Residence - Detail 11' },
    { image: '/The-Willow-Residence/willow_23.jpeg', alt: 'Willow Residence - Detail 12' },
    { image: '/The-Willow-Residence/willow_24.jpeg', alt: 'Willow Residence - Detail 13' },
    { image: '/The-Willow-Residence/willow_25.jpeg', alt: 'Willow Residence - Detail 14' },
    { image: '/The-Willow-Residence/willow_26.jpeg', alt: 'Willow Residence - Detail 15' },
    { image: '/The-Willow-Residence/willow_27.jpeg', alt: 'Willow Residence - Detail 16' },
    { image: '/The-Willow-Residence/willow_28.jpeg', alt: 'Willow Residence - Detail 17' },
    { image: '/The-Willow-Residence/willow_29.jpeg', alt: 'Willow Residence - Detail 18' }
  ]
},
{
  id: '1',
  title: 'Modern Minimalist Apartment',
  location: 'Hebbal - Bangalore',
  type: 'apartment',
  style: 'contemporary',
  budget: '20-40',
  area: '2,100 sq.ft',
  image: "https://images.unsplash.com/photo-1721613883249-6242e2effbf3",
  alt: 'Contemporary apartment with open floor plan, modern furniture, and abundant natural light',
  description: 'A tech professional\'s apartment designed with smart home integration, modular furniture, and a focus on work-life balance spaces.',
  completionYear: 2025,
  detailedDescription: 'Designed for a tech entrepreneur, this apartment embodies the principles of modern minimalism while incorporating smart home technology throughout. The open-plan layout creates distinct zones for work, relaxation, and entertainment, all controlled through integrated home automation. Sustainable materials and energy-efficient systems reflect the client\'s environmental consciousness.',
  challenges: [
  'Integrating extensive smart home technology seamlessly',
  'Creating separation in an open floor plan',
  'Accommodating frequent work-from-home requirements'],

  solutions: [
  'Concealed all wiring and technology infrastructure in custom millwork',
  'Used furniture placement and lighting to define zones',
  'Designed a dedicated home office with acoustic treatment'],

  materials: ['Engineered Stone', 'Smart Glass', 'Recycled Wood', 'LED Lighting Systems', 'Acoustic Panels'],
  timeline: '6 months from concept to completion',
  clientTestimonial: 'The perfect blend of technology and design. My apartment works as hard as I do, and looks amazing doing it.',
  clientName: 'Arjun Patel',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1540f734a-1764818215831.png",
  beforeAlt: 'Standard apartment layout with basic finishes and no smart features',
  gallery: [
  { image: '/modern-minimalistic/1.jpg', alt: 'Modern living room with smart lighting' },
  { image: '/modern-minimalistic/2.jpg', alt: 'Tech-integrated kitchen with touchscreen controls' },
  { image: '/modern-minimalistic/3.jpg', alt: 'Home office with ergonomic furniture' },
  { image: '/modern-minimalistic/4.jpg', alt: 'Minimalist bedroom with automated blinds' },
  { image: '/modern-minimalistic/5.jpg', alt: 'Entertainment area with home theater system' }]

},
{
  id: '2',
  title: 'Eco-Conscious Apartment',
  location: 'Sarjapur - Bangalore',
  type: 'apartment',
  style: 'minimalist',
  budget: '20-40',
  area: '1,600 sq.ft',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1465d717b-1764654993782.png",
  alt: 'Eco-friendly apartment with natural materials, indoor plants, and sustainable furniture',
  description: 'A sustainable living space featuring recycled materials, energy-efficient systems, and biophilic design principles.',
  completionYear: 2025,
  detailedDescription: 'This project showcases our commitment to sustainable luxury. Every material was chosen for its environmental credentials - from recycled wood flooring to low-VOC paints and energy-efficient appliances. The design incorporates biophilic elements with indoor gardens, natural light optimization, and air-purifying plants throughout. The result is a healthy, beautiful home with minimal environmental impact.',
  challenges: [
  'Sourcing sustainable materials within budget constraints',
  'Maintaining aesthetic appeal with eco-friendly options',
  'Educating clients on sustainable living practices'],

  solutions: [
  'Partnered with local sustainable material suppliers',
  'Demonstrated that eco-friendly can be equally beautiful',
  'Created a comprehensive sustainability guide for the homeowners'],

  materials: ['Recycled Wood', 'Bamboo Flooring', 'Low-VOC Paints', 'Recycled Glass', 'Organic Textiles'],
  timeline: '5 months from concept to completion',
  clientTestimonial: 'We wanted a home that aligned with our values. Hayat Interiors proved that sustainable living doesn\'t mean compromising on style.',
  clientName: 'Sneha & Rohan Kumar',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_14a1cb534-1765159447309.png",
  beforeAlt: 'Standard apartment with conventional materials and no green features',
  gallery: [
  { image: '/eco-cons/1.png', alt: 'Living room with indoor plants and natural materials' },
  { image: '/eco-cons/2.png', alt: 'Kitchen with recycled materials and energy-efficient appliances' },
  { image: '/eco-cons/3.png', alt: 'Bedroom with organic bedding and bamboo furniture' },
  { image: '/eco-cons/4.png', alt: 'Bathroom with water-saving fixtures' },
  { image: '/eco-cons/5.png', alt: 'Indoor garden area with vertical planters' },
  { image: '/eco-cons/6.png', alt: 'Balcony with composting setup' }]

},
{
  id: '3',
  title: 'Serene Sanctuary Villa',
  location: 'Whitefield, Bangalore',
  type: 'villa',
  style: 'contemporary',
  budget: '40-60',
  area: '3,500 sq.ft',
  image: "https://images.unsplash.com/photo-1721614463238-c327d3d42ccf",
  alt: 'Modern villa living room with floor-to-ceiling windows, neutral tones, and minimalist furniture',
  description: 'A contemporary villa blending modern aesthetics with traditional Indian craftsmanship, featuring custom woodwork and natural materials.',
  completionYear: 2025,
  detailedDescription: 'This stunning villa transformation showcases our signature approach of merging contemporary design principles with timeless Indian craftsmanship. The 3,500 sq.ft space features custom-designed teak wood panels, handcrafted brass fixtures, and locally-sourced natural stone. Every element was carefully curated to create a serene sanctuary that honors both modern living and cultural heritage.',
  challenges: [
  'Limited natural light in the central living areas',
  'Integrating traditional elements without compromising modern aesthetics',
  'Managing the project timeline during monsoon season'],

  solutions: [
  'Installed strategically placed skylights and light wells to maximize natural illumination',
  'Created custom fusion pieces that blend traditional motifs with contemporary forms',
  'Implemented weather-resistant materials and adjusted construction schedule'],

  materials: ['Teak Wood', 'Italian Marble', 'Handcrafted Brass', 'Natural Stone', 'Linen Fabrics'],
  timeline: '8 months from concept to completion',
  clientTestimonial: 'Hayat Interiors transformed our house into a home that truly reflects our values. The attention to detail and respect for our cultural preferences was exceptional.',
  clientName: 'Priya & Rajesh Sharma',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e83f340e-1764786603630.png",
  beforeAlt: 'Empty villa interior with bare walls and basic flooring before renovation',
  gallery: [
  { image: '/serene/1.png', alt: 'Villa living room with modern furniture and large windows' },
  { image: '/serene/2.png', alt: 'Contemporary kitchen with white cabinets and marble countertops' },
  { image: '/serene/3.png', alt: 'Master bedroom with wooden accents and neutral bedding' },
  { image: '/serene/4.png', alt: 'Dining area with wooden table and pendant lighting' },
  { image: '/serene/5.png', alt: 'Modern bathroom with glass shower and marble tiles' },
  { image: '/serene/6.png', alt: 'Outdoor patio area with comfortable seating' }]

},
/* {
  id: '4',
  title: 'Luxury Family Villa',
  location: 'Jubilee Hills, Hyderabad',
  type: 'villa',
  style: 'luxury',
  budget: '60+',
  area: '5,000 sq.ft',
  image: "https://images.unsplash.com/photo-1718164258728-839e6060e489",
  alt: 'Luxurious villa interior with grand staircase, crystal chandelier, and marble flooring',
  description: 'An opulent family villa featuring custom Italian marble, designer lighting, and spaces designed for multi-generational living.',
  completionYear: 2025,
  detailedDescription: 'This grand villa project exemplifies luxury living designed for a multi-generational Indian family. The 5,000 sq.ft space includes separate wings for privacy, a central gathering area for family celebrations, a traditional pooja room, and modern amenities including a home gym and entertainment lounge. Every detail, from the imported Italian marble to the custom-designed furniture, reflects the family\'s taste for refined elegance.',
  challenges: [
  'Balancing privacy needs with family togetherness',
  'Incorporating traditional elements in a luxury context',
  'Managing multiple stakeholder preferences'],

  solutions: [
  'Created distinct wings connected by a central family area',
  'Designed a stunning traditional pooja room as the home\'s spiritual center',
  'Facilitated collaborative design sessions with all family members'],

  materials: ['Italian Marble', 'Crystal Chandeliers', 'Imported Wallpapers', 'Custom Furniture', 'Gold Leaf Accents'],
  timeline: '14 months from concept to completion',
  clientTestimonial: 'Hayat Interiors created a home that brings our entire family together while respecting everyone\'s individual needs. It\'s truly magnificent.',
  clientName: 'The Reddy Family',
  beforeImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1e731c673-1764876554201.png",
  beforeAlt: 'Large empty villa space with basic construction and no finishes',
  gallery: [
  { image: "https://images.unsplash.com/photo-1682542686317-8cc26be245c6", alt: 'Grand entrance with marble flooring and chandelier' },
  { image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fcc5572d-1764796153387.png", alt: 'Formal living room with luxury furniture' },
  { image: "https://img.rocket.new/generatedImages/rocket_gen_img_1684c9628-1764671775296.png", alt: 'Master bedroom suite with sitting area' },
  { image: "https://img.rocket.new/generatedImages/rocket_gen_img_14142eac2-1764678676520.png", alt: 'Gourmet kitchen with premium appliances' },
  { image: "https://images.unsplash.com/photo-1572458773209-43823703e9ea", alt: 'Traditional pooja room with ornate details' },
  { image: "https://images.unsplash.com/photo-1699134821322-d330a77c8b6c", alt: 'Home entertainment lounge with bar' }]

}, */
];


const OurProjectsInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    location: '',
    style: '',
    budget: '',
    search: ''
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>(mockProjects);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const slidingImages = [
    { src: '/modern-minimalistic/1.jpg', alt: 'Modern living room' },
    { src: '/modern-minimalistic/2.jpg', alt: 'Kitchen view' },
    { src: '/eco-cons/1.png', alt: 'Eco friendly living room' },
    { src: '/serene/1.png', alt: 'Serene villa living' },
    { src: '/serene/2.png', alt: 'Contemporary kitchen' }
  ];

  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slidingImages.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [isAutoPlaying]);

  useEffect(() => {
    if (!isHydrated) return;

    let filtered = mockProjects;

    if (filters.type) {
      filtered = filtered.filter((project) => project.type === filters.type);
    }

    if (filters.location) {
      filtered = filtered.filter((project) =>
      project.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    if (filters.style) {
      filtered = filtered.filter((project) => project.style === filters.style);
    }

    if (filters.budget) {
      filtered = filtered.filter((project) => project.budget === filters.budget);
    }

    if (filters.search) {
      filtered = filtered.filter((project) =>
      project.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      project.description.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    setFilteredProjects(filtered);
  }, [filters, isHydrated]);

  const handleFilterChange = (filterType: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      type: '',
      location: '',
      style: '',
      budget: '',
      search: ''
    });
  };

  const handleViewDetails = (projectId: string) => {
    const project = mockProjects.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="w-full max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="h-12 bg-muted animate-pulse rounded-md mb-8" />
          <div className="h-32 bg-muted animate-pulse rounded-lg mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) =>
            <div key={i} className="h-96 bg-muted animate-pulse rounded-lg" />
            )}
          </div>
        </div>
      </div>);

  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] overflow-hidden bg-black">
        <div className="absolute inset-0 top-0">
          {slidingImages.map((img, index) => (
            <div
              key={img.src}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Image src={img.src} alt={img.alt} fill className="object-cover scale-105" priority={index === 0} />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
            </div>
          ))}
        </div>

        <div className="relative min-h-[60vh] flex items-center py-24 lg:py-32">
          <div className="w-full px-8 lg:px-20 max-w-[1400px] mx-auto">
            <div className="max-w-4xl space-y-6">
              <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-xl border border-white/20">
                <div className="w-1.5 h-1.5 bg-white rounded-full" />
                <span className="text-xs font-medium text-white/90 tracking-widest uppercase">
                  Our Portfolio
                </span>
              </div>

              <h1 className="font-headline text-5xl lg:text-7xl font-normal leading-[0.95] text-white tracking-tight">
                Our Projects
              </h1>

              <p className="text-lg lg:text-xl text-white/80 leading-relaxed max-w-2xl font-normal">
                Explore our portfolio of thoughtfully designed spaces that blend modern minimalism with timeless Indian craftsmanship.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Slide Controls (left/right) */}
      <button
        onClick={() => { setIsAutoPlaying(false); setCurrentSlide((prev) => (prev - 1 + slidingImages.length) % slidingImages.length); }}
        className="absolute left-8 top-[42vh] -translate-y-1/2 w-12 h-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300"
        aria-label="Previous slide"
      >
        <Icon name="ChevronLeftIcon" size={18} className="text-white/80" />
      </button>

      <button
        onClick={() => { setIsAutoPlaying(false); setCurrentSlide((prev) => (prev + 1) % slidingImages.length); }}
        className="absolute right-8 top-[42vh] -translate-y-1/2 w-12 h-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full flex items-center justify-center hover:bg-white/10 transition-all duration-300"
        aria-label="Next slide"
      >
        <Icon name="ChevronRightIcon" size={18} className="text-white/80" />
      </button>

      <style jsx>{`
        .transition-opacity { transition: opacity 1s ease-in-out; }
      `}</style>

      {/* Projects Section */}
      <section className="pb-24 pt-8 bg-white">
        <div className="w-full max-w-[1400px] mx-auto px-8 lg:px-20">
          <FilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters} />


          {filteredProjects.length === 0 ?
          <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-zinc-100 rounded-full mb-4">
                <Icon name="FolderOpenIcon" size={32} className="text-zinc-400" />
              </div>
              <h3 className="font-headline text-2xl font-normal text-black mb-2">
                No Projects Found
              </h3>
              <p className="text-zinc-600 font-normal mb-6">
                Try adjusting your filters to see more results
              </p>
              <button
              onClick={handleClearFilters}
              className="px-8 py-4 bg-black text-white font-medium rounded-full hover:scale-105 transition-all duration-300">

                Clear All Filters
              </button>
            </div> :

          <>
              <div className="flex items-center justify-between mb-8">
                <p className="text-zinc-600 text-sm font-normal">
                  Showing {filteredProjects.length} {filteredProjects.length === 1 ? 'project' : 'projects'}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredProjects.map((project) =>
              <ProjectCard
                key={project.id}
                project={project}
                onViewDetails={handleViewDetails} />

              )}
              </div>
            </>
          }
        </div>
      </section>

      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={handleCloseModal} />

    </div>);

};

export default OurProjectsInteractive;